import { html } from 'lit';
import { JVElement } from '../../lib/base';
const runtimeConfig = window;
const viteEnv = import.meta.env;
const API_URL = runtimeConfig.JUSTVUE_API_URL || viteEnv?.VITE_API_URL || '';
/**
 * Operator sign-in page. Matches the wireframe 01-login.html.
 * No tenant selector — operators are cross-tenant.
 * Dispatches `login-success` event on successful auth so the parent app shell
 * can re-check auth and transition to the dashboard.
 */
export class JvOperatorLogin extends JVElement {
    static { this.properties = {
        _email: { state: true },
        _password: { state: true },
        _error: { state: true },
        _pending: { state: true },
    }; }
    constructor() {
        super();
        this._email = '';
        this._password = '';
        this._error = null;
        this._pending = false;
    }
    async _handleSubmit(e) {
        e.preventDefault();
        if (this._pending)
            return;
        this._error = null;
        if (!this._email.trim()) {
            this._error = 'Please enter your work email.';
            return;
        }
        if (!this._password) {
            this._error = 'Please enter your password.';
            return;
        }
        this._pending = true;
        try {
            // Operators authenticate cross-tenant with a null tenantId.
            // The backend OperatorEndpoints require SystemRoles.Operator, and
            // AuthEndpoints validates credentials; tenantId is required by the
            // LoginRequest contract, so we pass the well-known "operator" guid.
            const operatorTenantId = '00000000-0000-0000-0000-000000000001';
            const response = await fetch(`${API_URL || window.location.origin}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: this._email.trim(),
                    password: this._password,
                    tenantId: operatorTenantId,
                }),
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                const message = err.error || err.message || 'Invalid credentials';
                this._error = message === 'invalid_credentials'
                    ? 'The email or password you entered is incorrect. Please try again.'
                    : message;
                return;
            }
            const data = await response.json();
            localStorage.setItem('jv_token', data.accessToken);
            localStorage.setItem('jv_expires', data.expiresAt);
            // Notify parent app shell
            this.dispatchEvent(new CustomEvent('login-success', { bubbles: true, composed: true }));
        }
        catch (err) {
            this._error = 'Unable to reach the server. Please check your connection and try again.';
        }
        finally {
            this._pending = false;
        }
    }
    _onEmailInput(e) {
        this._email = e.target.value;
    }
    _onPasswordInput(e) {
        this._password = e.target.value;
    }
    render() {
        return html `<main class="min-h-screen grid place-items-center px-4 py-12 bg-jv-bg">
      <div class="w-full max-w-sm">

        <div class="flex flex-col items-center text-center mb-6">
          <span class="grid place-items-center w-14 h-14 rounded-2xl bg-jv-primary text-white shadow-jv mb-4">
            <span class="jvi-shield text-[26px]" aria-hidden="true"></span>
          </span>
          <h1 class="font-extrabold text-jv-ink text-2xl tracking-tight">Just<span class="text-jv-primary">Vue</span></h1>
          <p class="text-sm font-semibold text-jv-ink-2 mt-1">Operator console</p>
        </div>

        <div class="jv-card">
          <div class="jv-card-head"><h3>Sign in</h3></div>
          <div class="jv-card-body">
            <p class="text-sm text-jv-ink-2 -mt-1 mb-5">
              Welcome back. Sign in to manage schools, relay fleet, and audit.
            </p>

            ${this._error
            ? html `<div class="flex items-start gap-2.5 rounded-xl bg-jv-danger-050 px-3.5 py-3 mb-4">
                  <span class="jvi-warning text-[16px] text-jv-danger mt-0.5 shrink-0" aria-hidden="true"></span>
                  <p class="text-sm text-jv-danger font-medium">${this._error}</p>
                </div>`
            : null}

            <form class="space-y-4" @submit=${this._handleSubmit}>
              <div>
                <label class="jv-label" for="login-email">Work email</label>
                <input
                  id="login-email"
                  type="email"
                  class="jv-input"
                  placeholder="you@opennology.com"
                  autocomplete="username"
                  .value=${this._email}
                  @input=${this._onEmailInput}
                />
              </div>

              <div>
                <div class="flex items-center justify-between">
                  <label class="jv-label" for="login-password">Password</label>
                  <a href="#" class="text-xs font-semibold text-jv-primary hover:underline">Forgot?</a>
                </div>
                <input
                  id="login-password"
                  type="password"
                  class="jv-input"
                  placeholder="Enter your password"
                  autocomplete="current-password"
                  .value=${this._password}
                  @input=${this._onPasswordInput}
                />
              </div>

              <div class="flex items-center gap-2 pt-0.5">
                <input id="login-remember" type="checkbox" class="w-4 h-4 rounded border-jv-border text-jv-primary" checked />
                <label for="login-remember" class="text-sm text-jv-ink-2 select-none">Keep me signed in on this device</label>
              </div>

              <button
                type="submit"
                class="inline-flex items-center justify-center gap-2 w-full rounded-xl font-semibold text-sm px-4 py-2.5 bg-jv-primary text-white hover:bg-[var(--jv-primary-600)] transition shadow-jv"
                ?disabled=${this._pending}
              >
                ${this._pending
            ? html `<span class="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span> Signing in…`
            : html `<span class="jvi-arrow-right text-[18px]"></span> Sign in`}
              </button>
            </form>

            <div class="flex items-center gap-3 my-5">
              <span class="flex-1 h-px bg-jv-border"></span>
              <span class="text-xs font-semibold text-jv-ink-2">or</span>
              <span class="flex-1 h-px bg-jv-border"></span>
            </div>

            <button
              class="flex items-center justify-center gap-2 w-full rounded-xl font-semibold text-sm px-4 py-2.5 bg-jv-surface border border-jv-border text-jv-ink hover:bg-jv-surface-2 transition"
            >
              <span class="jvi-key text-[18px]" aria-hidden="true"></span>
              Continue with Opennology SSO
            </button>

            <div class="mt-5 flex items-start gap-2.5 rounded-xl bg-jv-primary-050 px-3.5 py-3">
              <span class="jvi-lock text-[16px] text-jv-primary mt-0.5 shrink-0" aria-hidden="true"></span>
              <p class="text-xs text-jv-ink-2 leading-relaxed">
                Operator accounts are internal to Opennology. Sessions and every camera view are recorded to the audit log.
              </p>
            </div>
          </div>
        </div>

      </div>
    </main>`;
    }
}
